import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IReportingPeriodPermission } from "../../model/reporting/ReportingPeriodPermission";
import CreateReportingPeriodPermissionInteractor from "../../interactor/reporting/CreateReportingPeriodPermissionInteractor";
import GetReportingPeriodPermissionsInteractor from "../../interactor/reporting/GetReportingPeriodPermissionsInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import UpdateReportingPeriodPermissionInteractor from "../../interactor/reporting/UpdateReportingPeriodPermissionInteractor";
import { IUserCode } from "../../model/user/UserCode";
import { DateService } from "../../service/DateService";
import GetAuthorizedOperatorsInteractor from "../../interactor/reporting/GetAuthorizedOperatorsInteractor";

export interface TReportingPeriodPermissionPresenter
  extends TLoadingAwarePresenter {
  reportingPeriodPermissions?: TReportingPeriodPermissionPresentationModel[];
  users: IUserCode[];
  applicantId: number;
  reportingPeriodId: number;
}
export interface IReportingPeriodPermissionPresenter
  extends TReportingPeriodPermissionPresenter,
    TPresentable {
  setReportingPeriodPermissions(
    reportingPeriodPermissions?: IReportingPeriodPermission[]
  ): void;
  createReportingPeriodPermission(
    reportingPeriodPermission: IReportingPeriodPermission
  ): void;
  getReportingPeriodPermission(
    applicantId: number,
    reportingPeriodId: number
  ): void;
  updateReportingPeriodPermission(
    reportingPeriodPermission: IReportingPeriodPermission
  ): void;
  setUsers(users: IUserCode[]): void;
}

export interface TReportingPeriodPermissionPresentationModel {
  key: number;
  id: number;
  applicantReportingPeriodId: number;
  userCode: string;
  action: string;
  fromDate: string;
  toDate?: string;
  user?: string;
}

const defaultState: TReportingPeriodPermissionPresenter = {
  reportingPeriodPermissions: [],
  users: [],
  applicantId: NaN,
  reportingPeriodId: NaN
};

const ReportingPeriodPermissionPresentationModel = (
  data: IReportingPeriodPermission
): TReportingPeriodPermissionPresentationModel => {
  const fromDate = DateService().formatDate(data.fromDate);
  const toDate = data.toDate
    ? DateService().formatDate(data.toDate)
    : undefined;
  return { ...data, fromDate, toDate, key: data.id, user: "" };
};

const ReportingPeriodPermissionPresenter = withStore<
  IReportingPeriodPermissionPresenter,
  TReportingPeriodPermissionPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IReportingPeriodPermissionPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TReportingPeriodPermissionPresenter>();

    loader.start("reportingPeriodPermissionsLoaders");

    const assignUsers = async (
      reportingPeriodPermissions: TReportingPeriodPermissionPresentationModel[]
    ): Promise<TReportingPeriodPermissionPresentationModel[]> => {
      const { reportingPeriodId } =
        _store.getState<TReportingPeriodPermissionPresenter>();

      const users = await _application.container
        .resolve<GetAuthorizedOperatorsInteractor>("getAuthorizedOperators")
        .execute(reportingPeriodId);

      const reportingPeriodPermissionList = (
        reportingPeriodPermissions || []
      ).map((reportingPeriodPermission) => {
        const user =
          users &&
          users.find(
            (item) => reportingPeriodPermission.userCode === item.userCode
          );
        reportingPeriodPermission.user = user
          ? user.user
          : reportingPeriodPermission.userCode;
        return reportingPeriodPermission;
      });

      return reportingPeriodPermissionList;
    };

    const setReportingPeriodPermissions = async (
      reportingPeriodPermissions?: IReportingPeriodPermission[]
    ) => {
      if (reportingPeriodPermissions) {
        let reportingPeriodPermissionsPresentationList =
          reportingPeriodPermissions.map(
            ReportingPeriodPermissionPresentationModel
          );

        reportingPeriodPermissionsPresentationList = await assignUsers(
          reportingPeriodPermissionsPresentationList
        );
        _store.update({
          reportingPeriodPermissions: reportingPeriodPermissionsPresentationList
        });
        loader.stop("reportingPeriodPermissionsLoaders");
      } else {
        loader.stop("reportingPeriodPermissionsLoaders");
        _application.navigator.push({ pathname: "/error" });
      }
    };

    const setUsers = (users: IUserCode[]) => {
      _store.update({
        users
      });
    };

    const getReportingPeriodPermissions = async () => {
      try {
        loader.stop("reportingPeriodPermissionsLoaders");
        const applicantId =
          _store.getState<TReportingPeriodPermissionPresenter>().applicantId;
        const reportingPeriodId =
          _store.getState<TReportingPeriodPermissionPresenter>()
            .reportingPeriodId;

        let reportingPeriodPermissions = await _application.container
          .resolve<GetReportingPeriodPermissionsInteractor>(
            "getReportingPeriodPermissions"
          )
          .execute(applicantId, reportingPeriodId);

        let reportingPeriodPermissionPresentationList =
          reportingPeriodPermissions.map(
            ReportingPeriodPermissionPresentationModel
          );

        reportingPeriodPermissionPresentationList = await assignUsers(
          reportingPeriodPermissionPresentationList
        );

        _store.update({
          reportingPeriodPermissions: reportingPeriodPermissionPresentationList
        });
        loader.stop("reportingPeriodPermissionsLoaders");
      } catch (error) {
        loader.stop("reportingPeriodPermissionsLoaders");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const createReportingPeriodPermission = async (
      reportingPeriodPermission: IReportingPeriodPermission
    ) => {
      const applicantId =
        _store.getState<TReportingPeriodPermissionPresenter>().applicantId;
      const reportingPeriodId =
        _store.getState<TReportingPeriodPermissionPresenter>()
          .reportingPeriodId;
      reportingPeriodPermission.applicantReportingPeriodId = reportingPeriodId;
      try {
        await _application.container
          .resolve<CreateReportingPeriodPermissionInteractor>(
            "createReportingPeriodPermission"
          )
          .execute(applicantId, reportingPeriodPermission);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "reporting_period_permission_page.messages.create_reporting_period_permission_success"
            )
          );
        await getReportingPeriodPermissions();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const updateReportingPeriodPermission = async (
      reportingPeriodPermission: IReportingPeriodPermission
    ) => {
      try {
        const applicantId =
          _store.getState<TReportingPeriodPermissionPresenter>().applicantId;
        await _application.container
          .resolve<UpdateReportingPeriodPermissionInteractor>(
            "updateReportingPeriodPermission"
          )
          .execute(applicantId, reportingPeriodPermission);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "reporting_period_permission_page.messages.update_reporting_period_permission_success"
            )
          );
        await getReportingPeriodPermissions();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setReportingPeriodPermissions: setReportingPeriodPermissions,
      createReportingPeriodPermission: createReportingPeriodPermission,
      getReportingPeriodPermission: getReportingPeriodPermissions,
      setUsers,
      updateReportingPeriodPermission: updateReportingPeriodPermission
    };
  },
  defaultState
);

export default ReportingPeriodPermissionPresenter;
